import { Component, type ErrorInfo, type ReactNode } from 'react';
import { RefreshCw } from 'lucide-react';

// The app had no error boundary anywhere — `rg "ErrorBoundary|componentDidCatch"`
// returned nothing across 41k lines. Two consequences, both of which end in a
// blank white page with no explanation:
//
//   1. Any render throw in any route takes down the whole SPA.
//   2. More insidiously: `vite.config.ts` sets `skipWaiting` + `clientsClaim`
//      and chunk filenames are content-hashed. When a deploy lands while a tab
//      is open, the old index's lazy-chunk URLs 404, `React.lazy` rejects, and
//      the <Suspense> in App.tsx has no error sibling to catch it. Long-lived
//      tabs — which this app explicitly encourages, it's an installable PWA the
//      team leaves open all shift — are exactly the ones that hit this.
//
// Case 2 is why the recovery action is a hard reload rather than a re-render:
// re-rendering would just re-request the same dead chunk.

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/** Heuristic for "this looks like a stale deploy, not a code bug". */
function looksLikeStaleChunk(error: Error): boolean {
  const msg = `${error.name} ${error.message}`;
  return /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|ChunkLoadError/i.test(
    msg,
  );
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // No error-reporting service is wired up in this project, so the console is
    // the honest destination. Keep the component stack — it's what makes a
    // white-screen report actionable when someone pastes it into Slack.
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const stale = looksLikeStaleChunk(error);

    return (
      <div className="flex min-h-[60vh] items-center justify-center px-4">
        <div className="w-full max-w-md rounded-md border border-border/70 bg-background p-6 text-center">
          <h1 className="text-base font-semibold tracking-tight">
            {stale ? 'A new version was deployed' : 'Something broke on this page'}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {stale
              ? 'This tab is running an older build and can no longer load part of the app. Reloading picks up the new version.'
              : 'The rest of the app is fine — reloading this page should get you moving again. If it keeps happening, send this message to the team.'}
          </p>

          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-4 inline-flex h-9 items-center gap-2 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
          >
            <RefreshCw className="h-3.5 w-3.5" aria-hidden />
            Reload
          </button>

          {!stale ? (
            <details className="mt-4 text-left">
              <summary className="cursor-pointer text-2xs text-muted-foreground hover:text-foreground">
                Technical details
              </summary>
              <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap break-words rounded bg-muted p-2 text-3xs text-muted-foreground">
                {error.name}: {error.message}
              </pre>
            </details>
          ) : null}
        </div>
      </div>
    );
  }
}
